import { BookingStatus } from '$lib/model/booking';
import type { Widget } from '$lib/model/business';
import type { Offer } from '$lib/model/offer';
import type { Session } from '$lib/model/session';
import { apiStore } from '$lib/stores/api';
import type { WidgetMyBooking } from '$lib/widget/helper/myBooking';
import type { WidgetSurvey } from '$lib/widget/helper/survey';
import {
	bookingGetStartTime,
	type BookingSession,
	type WidgetActivity,
	type WidgetBookingGiftcard,
	type WidgetColorProfile,
	type WidgetContent,
	type WidgetCustomer,
	type WidgetCustomField,
	type WidgetGiftcardForm,
	type WidgetOffer,
	type WidgetPayment,
	type WidgetPrice
} from '$lib/widget/helper/widget';
import type { AxiosResponse } from 'axios';
import { DateTime } from 'luxon';
import { derived, get, writable } from 'svelte/store';

export const widgetConfig = writable<Widget>();
export const widgetColors = writable<WidgetColorProfile>();
export const widgetContent = writable<WidgetContent>();
export const widgetPrices = writable<WidgetPrice[] | undefined>(undefined);
export const widgetGiftcardForm = writable<WidgetGiftcardForm | undefined>(undefined);
export const widgetBookingExpiresAt = writable<DateTime | undefined>(undefined);
export const widgetBookingSaved = writable<boolean>(false);
export const widgetCustomerSaved = writable<boolean>(false);
export const widgetChatEnabled = writable<boolean>(false);
export const widgetBookingError = writable<string | undefined>(undefined);

const widgetPaymentDefinition = () => {
	const { subscribe, set, update } = writable<WidgetPayment | undefined>(undefined);

	return {
		subscribe,
		set,
		update,
		isBookingWithoutPayment: () => {
			const payment = get(widgetPayment);

			// No billing options means no payment required
			return payment !== undefined && Object.keys(payment.billingOptions).length === 0;
		},
		reset: () => {
			set(undefined);
		}
	};
};
export const widgetPayment = widgetPaymentDefinition();

// Use this store to force dark mode for the widget, even in an app light theme
export const widgetColorsForceDarkMode = writable<boolean>(false);

export type WidgetBooking = {
	id: string;
	activity?: WidgetActivity;
	pax?: number;
	date?: DateTime;
	offer?: WidgetOffer;
	customer: WidgetCustomer;
	note?: string;
	prices: Record<string, number>;
	status: BookingStatus;
	paymentType?: 'full' | 'deposit';
	sessions: BookingSession[];
	customFields: Record<WidgetCustomField['id'], string>;
};

export type WidgetFilter = {
	minTime: number | undefined;
	maxTime: number | undefined;
};

export function newWidgetBooking(): WidgetBooking {
	return {
		id: '',
		customer: {
			firstName: '',
			lastName: '',
			email: '',
			phone: '',
			phoneOptIn: false,
			emailOptIn: false,
			customFields: {}
		},
		prices: {},
		status: BookingStatus.Intent,
		sessions: [],
		customFields: {}
	};
}

export const widgetBooking = writable<WidgetBooking>(newWidgetBooking());
export const widgetMyBooking = writable<WidgetMyBooking | undefined>(undefined);
export const widgetSurvey = writable<WidgetSurvey | undefined | null>(undefined);
export const widgetCustomerHasAcceptedOffer = writable<WidgetOffer['id'] | undefined>();

type DateType = string;
type MinuteAfterMidnightType = string;
export type Proposal = {
	sessions: {
		[key: MinuteAfterMidnightType]: [Session['id']];
	};
	end: MinuteAfterMidnightType;
};
export type AvailabilityResult = {
	offers: {
		[key: DateType]: {
			[key: Offer['id']]: [Proposal];
		};
	};
};

export function retrieveAvailForDate(
	date: DateTime,
	widgetBooking: WidgetBooking
): Promise<void | AxiosResponse<AvailabilityResult>> {
	const out: AvailabilityResult = { offers: {} };

	return Promise.all([
		requestAvails(
			date.startOf('month'),
			date.startOf('month').plus({ days: 10 }),
			widgetBooking
		).then((result) => {
			out.offers = {
				...out.offers,
				...result.data.offers
			};
		}),
		requestAvails(
			date.startOf('month').plus({ days: 11 }),
			date.startOf('month').plus({ days: 20 }),
			widgetBooking
		).then((result) => {
			out.offers = {
				...out.offers,
				...result.data.offers
			};
		}),
		requestAvails(
			date.startOf('month').plus({ days: 21 }),
			date.endOf('month'),
			widgetBooking
		).then((result) => {
			out.offers = {
				...out.offers,
				...result.data.offers
			};
		})
	]).then(() => {
		widgetAvails.set({
			offers: {
				...get(widgetAvails).offers,
				...out.offers
			}
		});
	});
}

export function clearCacheAvails(): void {
	cacheAvails = {};
}
let cacheAvails: { [key: string]: AxiosResponse<AvailabilityResult> } = {};
function requestAvails(
	from: DateTime,
	to: DateTime,
	widgetBooking: WidgetBooking
): Promise<AxiosResponse<AvailabilityResult>> {
	const params = {
		startDate: from.toISODate(),
		endDate: to.toISODate(),
		pax: widgetBooking.pax,
		activityId: widgetBooking.activity?.id,
		bookingId: widgetBooking?.id
	};
	// Cache the request, except for the bookingId
	const key = JSON.stringify({ ...params, bookingId: undefined });
	if (cacheAvails[key]) {
		return Promise.resolve(cacheAvails[key]);
	}

	return get(apiStore)!
		.post<AvailabilityResult>(`/widget/${get(widgetConfig).hash}/availability`, {
			startDate: from.toISODate(),
			endDate: to.toISODate(),
			pax: widgetBooking.pax,
			activityId: widgetBooking.activity?.id,
			bookingId: widgetBooking?.id
		})
		.then((res) => {
			cacheAvails[key] = res;
			return res;
		});
}

// offers[$date][$offerId][$i]['sessions' => [$start => sessionId], 'price' => float]

export const widgetAvails = writable<AvailabilityResult>({ offers: {} });

export function newWidgetBookableGiftcard(giftcard: WidgetBookingGiftcard): void {
	widgetGiftcardForm.set({
		giftcard: giftcard,
		quantity: 1,
		sendDate: undefined,
		senderName: '',
		senderEmail: '',
		recipients: [],
		message: '',
		allowOptIn: false
	});
}

export const widgetFilters = writable<WidgetFilter>({
	minTime: undefined,
	maxTime: undefined
});

export const hasTimeslot = derived(
	[widgetAvails, widgetFilters, widgetBooking],
	([$widgetAvails, $widgetFilters, $widgetBooking]) =>
		(date: DateTime, offerId: WidgetOffer['id'] | undefined): boolean => {
			const offers = $widgetAvails.offers;
			const minTime = $widgetFilters.minTime;
			const maxTime = $widgetFilters.maxTime;

			if (!minTime && !maxTime) return hasOffer(date, offerId, offers);
			const d = date.toISODate();
			if (!d || !offers[d]) return false;
			return Object.keys(offers[d])
				.filter((oId) => !offerId || oId === offerId)
				.some((oId) =>
					offers[d][oId].some(
						(proposal) =>
							(!$widgetFilters.minTime ||
								+Object.keys(proposal.sessions)[0] >= $widgetFilters.minTime) &&
							(!$widgetFilters.maxTime || +proposal.end <= $widgetFilters.maxTime)
					)
				);
		}
);

function hasOffer(
	date: DateTime,
	offerId: string | undefined,
	offers: AvailabilityResult['offers']
): boolean {
	const d = date.toISODate();
	if (!d) return true;
	if (!offers[d]) return false;
	return offerId ? !!offers[d][offerId] : Object.keys(offers[d]).length > 0;
}

let cacheKeyPrices: string = '';
export function getPricesFromApi(
	offerId: WidgetOffer['id'] | undefined = undefined
): Promise<void> {
	const key =
		(offerId ?? get(widgetBooking).offer?.id ?? '-') +
		'.' +
		(get(widgetBooking).date?.toISODate() ?? '-');
	if (cacheKeyPrices !== key) {
		let loadingKey = key;
		return get(apiStore)!
			.get<Record<string, WidgetPrice>>(
				`/widget/${get(widgetConfig).hash}/prices?offerId=${offerId ?? get(widgetBooking).offer?.id}&date=${get(widgetBooking).date?.toISODate()}`
			)
			.then((res) => {
				if (loadingKey !== key) return;
				cacheKeyPrices = key;
				let availablePrices: WidgetPrice[] = [...Object.values(res.data)];
				widgetPrices.set(availablePrices);
			});
	} else {
		return Promise.resolve();
	}
}

export function getPricesAvailables(): WidgetPrice[] {
	return get(widgetPrices)!.filter((price) => {
		const booking = get(widgetBooking);
		if (!booking) return [];

		if (booking.pax && price.minPax > booking.pax) {
			// Filter out prices that are not available for the current pax
			return false;
		}
		if (booking.pax && price.maxPax < booking.pax) {
			return false;
		}

		// Check if this price is available within the selected time period
		let matchingTimePeriod = false;
		const time = bookingGetStartTime(booking);
		if (time) {
			Object.values(price.timePeriods).forEach((timePeriod) => {
				if (time && time >= timePeriod.start && time <= timePeriod.end) {
					matchingTimePeriod = true;
					return;
				}
			});
		}

		return matchingTimePeriod;
	});
}

export let widgetOffers = derived(
	[widgetBooking, widgetContent],
	([$widgetBooking, $widgetContent]): WidgetOffer[] => {
		return Object.values($widgetContent.offers)
			.filter(
				(o) => $widgetBooking.activity && o.activityIds.includes($widgetBooking.activity.id)
			)
			.sort(
				(a, b) =>
					$widgetContent.offers[a.id].sortOrder - $widgetContent.offers[b.id].sortOrder
			);
	}
);
